<template>
  <div>
    <BaseTitle :title="$t(edit ? 'content.edit_modules' : 'content.add_modules')" :yearPeriode="titleData" />
    <v-row>
      <v-col cols="6" md="6">
        <form @submit.prevent="submit">
          <v-card class="mb-8">
            <div class="pa-8">
              <div class="app-section-title mb-6">{{ $t('content.modules_name') }}</div>
              <form-group :name="$t('content.modules_name')" :validator="$v.form.moduleName">
                <template slot-scope="{ attrs }">
                  <v-text-field
                    v-model="form.moduleName"
                    :disabled="isEditable"
                    v-bind="attrs"
                    @input="$v.form.moduleName.$touch()"
                    @blur="$v.form.moduleName.$touch()"
                  >
                    <template #label>
                      {{ $t('content.modules_name') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-text-field>
                </template>
              </form-group>
              <form-group :name="$t('form.status')" :validator="$v.form.locked">
                <template slot-scope="{ attrs }">
                  <v-select v-model="form.locked" v-bind="attrs" @input="$v.form.locked.$touch()" @blur="$v.form.locked.$touch()" :items="lockItems">
                    <template #label>
                      {{ $t('form.status') }}
                      <span class="error--text">*</span>
                    </template>
                  </v-select>
                </template>
              </form-group>
              <div>
                <v-row>
                  <v-col cols="6" md="6">
                    <form-group :name="$t('content.available_from')" :validator="$v.form.availableFromDate">
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="menuFromDate"
                          v-model="menuFromDate"
                          :close-on-content-click="false"
                          :return-value.sync="menuFromDate"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on, attrs:attrActivator }">
                            <v-text-field v-model="form.availableFromDate" readonly v-bind="{ ...attrActivator, ...attrs }" v-on="on">
                              <template #label>
                                {{ $t('content.available_from') }}
                                <span class="error--text">*</span>
                              </template>
                              <template #append>
                                <svg-icon icon="calendar" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker v-model="form.availableFromDate" :locale="$i18n.locale">
                            <v-spacer />
                            <v-btn text color="primary" @click="menuFromDate = false">{{ $t('cancel') }}</v-btn>
                            <v-btn text color="primary" @click="$refs.menuFromDate.save(form.availableFromDate)">{{ $t('ok') }}</v-btn>
                          </v-date-picker>
                        </v-menu>
                      </template>
                    </form-group>
                  </v-col>
                  <v-col cols="6" md="4">
                    <form-group :name="$t('content.available_time')" v-if="manualFrom === false" :validator="$v.form.availableFromTime" no-gutter>
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="menuFromTime"
                          v-model="menuFromTime"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="menuFromTime"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs: attrActivator }">
                            <v-text-field v-model="form.availableFromTime" readonly v-bind="{ ...attrActivator, ...attrs }" v-on="on">
                              <template #label>
                                {{ $t('content.available_time') }}
                                <span class="error--text">*</span>
                              </template>
                              <template #append>
                                <svg-icon icon="clock" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-time-picker
                            v-model="form.availableFromTime"
                            :locale="$i18n.locale"
                            format="24hr"
                            @click:minute="$refs.menuFromTime.save(form.availableFromTime)"
                          />
                        </v-menu>
                      </template>
                    </form-group>
                    <form-group v-else-if="manualFrom === true" :validator="$v.form.availableFromTime">
                      <v-text-field v-model="form.availableFromTime" type="time" :label="$t('content.available_time')" />
                    </form-group>
                  </v-col>
                  <v-col cols="6" md="2">
                    <form-group>
                      <div class="d-flex align-center justify-space-between">
                        <v-switch v-model="manualFrom" />
                      </div>
                    </form-group>
                  </v-col>
                </v-row>
              </div>
              <div>
                <form-group :name="$t('content.unlock_schedule')">
                  <div class="d-flex align-center justify-space-between">
                    <v-switch v-model="form.scheduled" :label="$t('content.file.file_unlock_with_schedule')" />
                  </div>
                </form-group>
                <v-row>
                  <v-col cols="6" md="6">
                    <form-group :name="$t('form.date')" :validator="$v.form.scheduleDate">
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="menuFromStatus"
                          v-model="menuFromStatus"
                          :close-on-content-click="false"
                          :return-value.sync="menuFromStatus"
                          transition="scale-transition"
                          offset-y
                          min-width="290px"
                        >
                          <template #activator="{ on, attrs:attrActivator }">
                            <v-text-field
                              v-model="form.scheduleDate"
                              :label="$t('form.date')"
                              readonly
                              v-bind="{ ...attrActivator, ...attrs }"
                              v-on="on"
                              @blur="$v.form.scheduleDate"
                              :disabled="!form.scheduled"
                            >
                              <template #append>
                                <svg-icon icon="calendar" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker v-model="form.scheduleDate" :locale="$i18n.locale">
                            <v-spacer />
                            <v-btn text color="primary" @click="menuFromStatus = false">{{ $t('cancel') }}</v-btn>
                            <v-btn text color="primary" @click="$refs.menuFromStatus.save(form.scheduleDate)">{{ $t('ok') }}</v-btn>
                          </v-date-picker>
                        </v-menu>
                      </template>
                    </form-group>
                  </v-col>
                  <v-col cols="6" md="4">
                    <form-group :name="$t('form.time')" v-if="manualSchedule === false" no-gutter :validator="$v.form.scheduleTime">
                      <template slot-scope="{ attrs }">
                        <v-menu
                          ref="pickerTimeStatus"
                          v-model="pickerTimeStatus"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="pickerTimeStatus"
                          transition="scale-transition"
                          offset-y
                          max-width="290px"
                          min-width="290px"
                        >
                          <template v-slot:activator="{ on, attrs: attrActivator }">
                            <v-text-field
                              v-model="form.scheduleTime"
                              :label="$t('form.time')"
                              readonly
                              v-bind="{ ...attrActivator, ...attrs }"
                              v-on="on"
                              :disabled="!form.scheduled"
                            >
                              <template #append>
                                <svg-icon icon="clock" class="primary--text mr-3" />
                              </template>
                            </v-text-field>
                          </template>
                          <v-time-picker
                            v-model="form.scheduleTime"
                            @blur="$v.form.scheduleTime"
                            :locale="$i18n.locale"
                            format="24hr"
                            @click:minute="$refs.pickerTimeStatus.save(form.scheduleTime)"
                          />
                        </v-menu>
                      </template>
                    </form-group>
                    <v-text-field type="time" v-if="manualSchedule === true" v-model="form.scheduleTime" :label="$t('form.time')" />
                  </v-col>
                  <v-col cols="6" md="2">
                    <form-group>
                      <div class="d-flex align-center justify-space-between">
                        <v-switch v-model="manualSchedule" :disabled="!form.scheduled" />
                      </div>
                    </form-group>
                  </v-col>
                </v-row>
              </div>
              <div class="app-section-title mb-6">{{ $t('form.desc') }}</div>
              <form-group :validator="$v.form.description">
                <template slot-scope="{ attrs }">
                  <text-editor v-bind="attrs" v-model="form.description" />
                </template>
              </form-group>
            </div>
          </v-card>
        </form>
      </v-col>
      <v-col cols="6" md="6">
        <form @submit.prevent="submit">
          <v-card class="mb-8">
            <div class="pa-8">
              <v-row>
                <v-col md="9">
                  <div class="app-section-title">{{ $t('content.assign_modules') }}</div>
                </v-col>
                <v-col md="3">
                  <v-menu bottom origin="center center" transition="scale-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn color="primary" class="btn-primary-custom" dark v-bind="attrs" v-on="on">
                        {{ $t('add') }}
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item v-for="(item, i) in viewType" :key="i">
                        <v-list-item-title v-if="item.value === 1">
                          <v-btn text class="text-dropdown" @click="assignMod(1)">
                            {{ item.text }}
                          </v-btn>
                        </v-list-item-title>
                        <v-list-item-title v-if="item.value === 2">
                          <v-btn text class="text-dropdown" @click="assignMod(2)">
                            {{ item.text }}
                          </v-btn>
                        </v-list-item-title>
                        <v-list-item-title v-if="item.value === 3">
                          <v-btn text class="text-dropdown" @click="assignMod(3)">
                            {{ item.text }}
                          </v-btn>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-col>
              </v-row>
              <!-- file -->
              <div style="margin-top: 1.5rem;">
                <div v-for="itemFiles in form.files" :key="itemFiles.fileId">
                  <v-card class="d-flex justify-space-between mb-6 rounded-lg" color="secondary lighten-5" flat tile>
                    <v-card class="pa-3 mt-2 rounded-lg d-flex d-inline" outlined tile color="secondary lighten-5">
                      <svg-icon icon="modules_2" width="30" height="30" class="primary--text  mr-4" />
                      <span>
                        {{ itemFiles.fileName }}
                      </span>
                    </v-card>
                    <v-card class="pa-3  mt-2rounded-lg" outlined tile color="secondary lighten-5">
                      <v-btn text @click="deleteFile(itemFiles)" class="mr-1"><svg-icon icon="delete" class="red--text"/></v-btn>
                    </v-card>
                  </v-card>
                </div>
              </div>

              <!-- assignement -->
              <div>
                <div v-for="itemAssign in form.assignments" :key="itemAssign.moduleAssignmentId">
                  <v-card class="d-flex justify-space-between mb-6 rounded-lg" color="secondary lighten-5" flat tile>
                    <v-card class="pa-3 mt-2 rounded-lg d-flex d-inline" outlined tile color="secondary lighten-5">
                      <svg-icon icon="modules_1" width="30" height="30" class="primary--text  mr-4" />
                      <span>
                        {{ itemAssign.assignmentTitle }}
                      </span>
                    </v-card>
                    <v-card class="pa-3  rounded-lg" outlined tile color="secondary lighten-5">
                      <v-btn text @click="deleteAssignment(itemAssign)" class="mr-1"><svg-icon icon="delete" class="red--text"/></v-btn>
                    </v-card>
                  </v-card>
                </div>
              </div>

              <!-- pages -->

              <div>
                <div v-for="itempPages in form.pages" :key="itempPages.modulePageId">
                  <v-card class="d-flex justify-space-between mb-6 rounded-lg" color="secondary lighten-5" flat tile>
                    <v-card class="pa-3 mt-2 rounded-lg d-flex d-inline" outlined tile color="secondary lighten-5">
                      <svg-icon icon="modules_3" width="30" height="30" class="primary--text  mr-4" />
                      <span>
                        {{ itempPages.title }}
                      </span>
                    </v-card>
                    <v-card class="pa-3 rounded-lg" outlined tile color="secondary lighten-5">
                      <v-btn text @click="deletePages(itempPages)" class="mr-1"><svg-icon icon="delete" class="red--text"/></v-btn>
                    </v-card>
                  </v-card>
                </div>
              </div>
              <NoData v-if="form.pages.length === 0 && form.assignments.length === 0 && form.files.length === 0" />
            </div>
          </v-card>
          <div class="d-flex justify-end align-center">
            <v-btn color="primary" class="btn-primary-custom mr-3" @click="back">{{ $t('cancel') }}</v-btn>
            <v-btn color="primary" class="btn-primary-custom" @click="submit" :loading="loading">
              {{ $t('submit') }}
            </v-btn>
          </div>
        </form>
        <!-- Dialog Assign Module -->
        <v-dialog v-model="dialogAssign" width="1000" persistent>
          <v-card>
            <v-card-title class="headline secondary white--text">
              {{ $t(viewTypeLabel) }}
            </v-card-title>
            <div>
              <div class="move-list pa-8">
                <b>{{ $t('select_below_list') }} {{ $t(viewTypeLabel) }} {{ $t('here') }}</b>
                <div class="move-list-title mt-10">
                  <v-text-field dense filled dark rounded hide-details :label="$t('search')" single-line v-model="search">
                    <template #append>
                      <v-icon color="white">mdi-magnify</v-icon>
                    </template>
                  </v-text-field>
                </div>
                <div class="move-list-content">
                  <ul v-if="viewTypeData === 1 && filterFile.length">
                    <li v-for="item in filterFile" :key="item.fileId">
                      <v-checkbox color="secondary" v-model="tempFiles" :value="item.fileId" :label="`${item.fileName}`" />
                    </li>
                  </ul>
                  <ul v-if="viewTypeData === 2 && filterAssignment.length">
                    <li v-for="item in filterAssignment" :key="item.assignmentId">
                      <v-checkbox color="secondary" v-model="tempAssignments" :value="item.assignmentId" :label="`${item.assignmentTitle}`" />
                    </li>
                  </ul>
                  <ul v-if="viewTypeData === 3 && filterPages.length">
                    <li v-for="item in filterPages" :key="item.pagesId">
                      <v-checkbox color="secondary" v-model="tempPages" :value="item.pagesId" :label="`${item.title}`" />
                    </li>
                  </ul>

                  <NoData
                    v-if="
                      (viewTypeData === 1 && filterFile.length === 0) ||
                        (viewTypeData === 2 && filterAssignment.length === 0) ||
                        (viewTypeData === 3 && filterPages.length === 0)
                    "
                  />
                </div>
              </div>
            </div>
            <v-divider />
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                class="btn-primary-custom mt-2 mb-2 mr-4"
                @click="
                  dialogAssign = false;
                  search = '';
                "
                >{{ $t('submit') }}</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BaseTitle from '@/components/base/Title';
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import NoData from '@/components/table/NoData';
import TextEditor from '@/components/base/TextEditor.vue';

export default {
  name: 'CreateEditModules',
  components: {
    TextEditor,
    BaseTitle,
    NoData
  },
  props: {
    isClass: {
      type: Boolean,
      default: false
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    isStudent: {
      type: Boolean,
      default: false
    }
  },
  validations() {
    return {
      form: {
        moduleName: { required },
        locked: { required },
        availableFromDate: { required },
        availableFromTime: { required },
        scheduleDate: this.form.scheduled === true ? { required } : {},
        scheduleTime: this.form.scheduled === true ? { required } : {}
      }
    };
  },
  computed: {
    ...mapGetters({
      classLevel: 'QuizTeacher/classLevel',
      tokenBearer: 'Auth/token',
      user: 'Auth/user',
      nameCourse: 'CourseList/nameCourse'
    }),
    lockItems() {
      return [
        { text: this.$t('content.published'), value: false },
        { text: this.$t('content.unpublished'), value: true }
      ];
    },
    filterFile() {
      if (this.search) {
        return this.filesList.filter(item => {
          return item.fileName.startsWith(this.search);
        });
      } else {
        return this.filesList;
      }
    },
    filterAssignment() {
      if (this.search) {
        return this.dateList.filter(item => {
          return item.assignmentTitle.startsWith(this.search);
        });
      } else {
        return this.dateList;
      }
    },
    filterPages() {
      if (this.search) {
        return this.pagesList.filter(item => {
          return item.title.startsWith(this.search);
        });
      } else {
        return this.pagesList;
      }
    },
    viewType() {
      return [
        { value: 1, text: this.$t('form.file') },
        { value: 2, text: this.$t('menu.assignment') },
        { value: 3, text: this.$t('content.pages') }
      ];
    },
    viewTypeLabel() {
      const mapping = {
        1: 'form.files',
        2: 'menu.assignment'
      };
      return mapping[this.viewTypeData] || 'content.pages';
    }
  },
  data: () => ({
    form: {
      moduleName: '',
      locked: '',
      scheduled: '',
      scheduleDate: '',
      scheduleTime: '',
      scheduleDatetime: '',
      availableFromDate: '',
      availableFromTime: '',
      availableFrom: null,
      description: '',
      files: [],
      assignments: [],
      pages: []
    },
    tempFiles: [],
    tempAssignments: [],
    tempPages: [],
    search: '',
    dateList: [],
    filesList: [],
    pagesList: [],
    viewItem: null,
    menuFromStatus: false,
    manualFrom: false,
    menuFromDate: false,
    menuFromTime: false,
    manualSchedule: false,
    pickerTimeStatus: false,
    loadingButton: false,
    dataClass: '',
    loading: false,
    edit: false,
    dialogAssign: false,
    viewTypeData: '',
    titleData: ''
  }),
  watch: {
    tempFiles(newVal) {
      const indeks = newVal.reduce((acc, row) => {
        const s = this.filesList.findIndex(res => res.fileId === row);
        if (s !== -1) {
          acc.push(s);
        }
        return acc;
      }, []);
      this.form.files = [];
      this.filesList.forEach((row, idx) => {
        if (indeks.includes(idx)) {
          this.form.files.push(row);
        }
      });
    },
    tempAssignments(newVal1) {
      const indeks = newVal1.reduce((acc, row) => {
        const s = this.dateList.findIndex(res => res.assignmentId === row);
        if (s !== -1) {
          acc.push(s);
        }
        return acc;
      }, []);
      this.form.assignments = [];
      this.dateList.forEach((row, idx) => {
        if (indeks.includes(idx)) {
          this.form.assignments.push(row);
        }
      });
    },
    tempPages(newVal2) {
      const indeks = newVal2.reduce((acc, row) => {
        const s = this.pagesList.findIndex(res => res.pagesId === row);
        if (s !== -1) {
          acc.push(s);
        }
        return acc;
      }, []);
      this.form.pages = [];
      this.pagesList.forEach((row, idx) => {
        if (indeks.includes(idx)) {
          this.form.pages.push(row);
        }
      });
    }
  },
  methods: {
    deleteAssignment(item) {
      this.form.assignments = this.form.assignments.filter(function(obj) {
        return obj.assignmentId !== item.assignmentId;
      });

      var index = this.tempAssignments.indexOf(item.assignmentId);
      this.tempAssignments.splice(index, 1);
    },
    deletePages(item) {
      this.form.pages = this.form.pages.filter(function(obj) {
        return obj.pagesId !== item.pagesId;
      });

      var index = this.tempPages.indexOf(item.pagesId);
      this.tempPages.splice(index, 1);
    },
    deleteFile(item) {
      this.form.files = this.form.files.filter(function(obj) {
        return obj.fileId !== item.fileId;
      });

      var index = this.tempFiles.indexOf(item.fileId);
      this.tempFiles.splice(index, 1);
    },
    assignMod(id) {
      if (id === 1) {
        this.viewTypeData = 1;
      } else if (id === 2) {
        this.viewTypeData = 2;
      } else if (id === 3) {
        this.viewTypeData = 3;
      }
      this.dialogAssign = true;
    },
    getData() {
      this.$store.dispatch('Modules/fetchDetail', this.$route.params.id).then(ras => {
        Object.keys(this.form).forEach(form => {
          this.form[form] = ras[form];
          this.form.files = ras.moduleFiles;
          this.form.pages = ras.modulePages;
          this.form.assignments = ras.moduleAssignments;
        });
        setTimeout(() => {
          this.tempFiles = ras.moduleFiles.map(a => a.fileId);
          this.tempPages = ras.modulePages.map(a => a.pageId);
          this.tempAssignments = ras.moduleAssignments.map(a => a.assignmentId);
        }, 10);
      });
    },
    getDataList() {
      const { classId, courseId } = this.$route.params;
      this.$store
        .dispatch('Assignment/fetchListFilter', {
          classId,
          courseId,
          classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : '',
          search: this.search
        })
        .then(res => (this.dateList = res))
        .catch(e => this.$store.dispatch('general/showErrorReqNotification', e));
    },
    getDataListFiles() {
      const { classId, courseId } = this.$route.params;
      this.$store
        .dispatch('Modules/fetchListFiles', {
          classId,
          courseId,
          classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : '',
          search: this.search
        })
        .then(res => (this.filesList = res))
        .catch(e => this.$store.dispatch('general/showErrorReqNotification', e));
    },
    getDataPages() {
      this.loadingLoad = true;
      this.$store
        .dispatch('Pages/getDataAll', {
          classroomId: this.$route.params.classId,
          courseId: this.$route.params.courseId,
          classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : '',
          search: this.search
        })
        .then(res => {
          this.pagesList = res;
        })
        .finally(() => {
          this.loadingLoad = false;
        });
    },
    getDataGradeId() {
      this.loading = true;
      this.$store
        .dispatch('QuizTeacher/fetchListGradeId', {
          classId: this.$route.params.classId,
          courseId: this.$route.params.courseId,
          classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
        })
        .then(res => {
          this.form.gradeId = res.gradeId;
        })
        .finally(() => (this.loading = false));
    },
    getDataClass() {
      if (!this.$route.params.classSectionId) {
        this.$store
          .dispatch('Class/fetchClassroomCourseName', { classId: this.$route.params.classId, courseId: this.$route.params.courseId })
          .then(res => {
            if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
              this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            } else {
              this.$store.dispatch('Class/fetchListLiteDetail', this.$route.params.classId).then(resa => {
                this.dataClass = resa;
                this.titleData = res + ` ( ${resa.yearPeriode} ) `;
              });
            }
          });
      } else {
        this.$store.dispatch('ClassSection/getListDetail', { classSectionId: this.$route.params.classSectionId }).then(res => {
          if (this.user.cerdasTypeId === 2 || this.user.cerdasTypeId === null) {
            this.$store.dispatch('SchoolClass/fetchDetail', this.$route.params.classId).then(resa => {
              this.dataClass = resa;
              this.titleData = res.classroomName + ` ${res.numberClass}` + ` ( ${resa.yearPeriode} ) `;
            });
          }
        });
      }
    },
    getClassLevel() {
      this.$store.dispatch('QuizTeacher/fetchClassLevelOrCourse');
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      }

      if (this.form.scheduled === true) {
        this.form.scheduleDatetime = this.form.scheduleDate.concat(' ', this.form.scheduleTime);
      }

      if (this.form.availableFromDate) {
        this.form.availableFrom = this.form.availableFromDate.concat(' ', this.form.availableFromTime);
      }

      if (!this.form.description) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_form_desc') });
        return;
      }

      let files = this.form.files.map(a => a.fileId);
      let pages = this.form.pages.map(a => a.pagesId);
      let assignments = this.form.assignments.map(a => a.assignmentId);

      if (!files.length && !pages.length && !assignments.length) {
        this.$store.dispatch('general/showErrorNotification', { message: this.$t('alert.alert_form_module') });
        return;
      }

      const input = {
        moduleName: this.form.moduleName,
        scheduleDatetime: this.form.scheduleDatetime,
        scheduled: this.form.scheduled,
        locked: this.form.locked,
        description: this.form.description,
        assignments: assignments,
        availableFrom: this.form.availableFrom,
        files: files,
        pages: pages,
        classId: parseInt(this.$route.params.classId),
        courseId: parseInt(this.$route.params.courseId),
        classSectionId: this.$route.params.classSectionId ? this.$route.params.classSectionId : ''
      };

      this.loading = true;
      const payload = { ...(this.edit ? { id: this.$route.params.id } : null), ...input };

      this.$store
        .dispatch(`Modules/${this.edit ? 'update' : 'save'}`, payload)
        .then(() =>
          this.$router.push({
            name:
              this.edit && !this.$route.params.classSectionId
                ? 'ModulesTeacherUpdateSuccess'
                : !this.edit && !this.$route.params.classSectionId
                ? 'ModulesTeacherSuccess'
                : this.edit && this.$route.params.classSectionId
                ? 'ModulesSectionTeacherUpdateSuccess'
                : 'ModulesSectionTeacherSuccess'
          })
        )
        .finally(() => (this.loading = false));
    },
    back() {
      if (this.isClass && !this.isStudent) {
        if (!this.$route.params.classSectionId) {
          this.$router.push({ name: 'ModulesIndexClass' });
        } else {
          this.$router.push({ name: 'ModulesSectionIndexClass' });
        }
      } else if (this.isClass && this.isStudent) {
        this.$router.push({ name: 'ModulesStudentIndexClass' });
      } else {
        this.$router.push({ name: 'ModulesIndex' });
      }
    }
  },
  created() {
    this.getDataList();
    this.getDataListFiles();
    this.getDataPages();
    setTimeout(() => {
      if (this.$route.params.id) {
        this.edit = true;
        this.getData();
      }
      if (this.isClass) {
        this.getDataGradeId();
        this.getDataClass();
      } else {
        this.getClassLevel();
        localStorage.removeItem('courseName');
      }
    }, 10);
  }
};
</script>

<style lang="scss">
html,
body {
  height: 100%;
}

body {
  height: 100%;
}
.move-list {
  &-title {
    background: #00bf63;
    color: #fff;
    font-family: Montserrat;
    font-weight: 800;
    border-radius: 8px 8px 0 0;
    padding: 10px;
    width: 100%;
  }

  &-content {
    border: 1px solid #d8d8d8;
    border-radius: 0 0 8px 8px;
    padding: 10px;
    overflow: scroll;
    overflow-x: hidden;
    width: 100%;
    height: 18em;
    line-height: 2em;

    ul {
      list-style: none;
      padding: 0;

      li {
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #d8d8d8;
        padding: 10px 15px;
        align-items: center;

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }
}
</style>
